import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Hexagon, ArrowLeft, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { API } = useAuth();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-zinc-400 mb-4">Invalid reset link. No token provided.</p>
          <button onClick={() => navigate('/forgot-password')} className="text-sm text-amber-500 hover:text-amber-400 font-medium">
            Request a new reset link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 honeycomb-bg opacity-20" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm"
      >
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-sm mb-8 transition-colors"
          data-testid="reset-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="relative">
            <Hexagon className="w-10 h-10 text-amber-500 fill-amber-500/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
            </div>
          </div>
          <div>
            <h1 className="font-heading font-black text-2xl text-white">Reset Password</h1>
            <p className="text-sm text-zinc-500">Enter your new password</p>
          </div>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-xl p-6 text-center"
          >
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            </div>
            <h2 className="text-lg font-bold text-white mb-2">Password reset!</h2>
            <p className="text-sm text-zinc-400 mb-6">
              Your password has been updated. You can now log in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="honey-btn px-8 py-2.5 rounded-xl text-sm font-bold"
              data-testid="reset-go-login"
            >
              Go to Login
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400" data-testid="reset-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-sm text-zinc-400">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  data-testid="reset-password-input"
                  type={showPw ? 'text' : 'password'}
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 pr-10 focus:border-amber-500/50 focus:ring-amber-500/20"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password" className="text-sm text-zinc-400">Confirm Password</Label>
              <Input
                id="confirm-new-password"
                data-testid="reset-confirm-password-input"
                type={showPw ? 'text' : 'password'}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className={`bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 focus:border-amber-500/50 focus:ring-amber-500/20 ${
                  passwordsMismatch ? 'border-red-500/50' : passwordsMatch ? 'border-emerald-500/50' : ''
                }`}
                required
                minLength={6}
              />
              {passwordsMatch && <p className="text-xs text-emerald-400">Passwords match</p>}
              {passwordsMismatch && <p className="text-xs text-red-400">Passwords do not match</p>}
            </div>
            <button
              type="submit"
              disabled={loading || passwordsMismatch}
              className="honey-btn w-full py-3 rounded-xl text-sm font-bold disabled:opacity-50"
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
